import React, { useEffect, useRef, useState } from 'react';

export const useInView = (options = {}) => {
  const { threshold = 0.1, rootMargin = '0px 0px -5% 0px', once = true } = options;
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    // Immediate check on mount - if any part of element is in viewport, show right away.
    // Elements below the fold (r.top >= viewport height) wait for the observer.
    const check = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      if (r.top < vh && r.bottom > 0) {
        setInView(true);
        return true;
      }
      return false;
    };
    if (check()) return;

    if (typeof IntersectionObserver === 'undefined') {
      const onScroll = () => { check(); };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

const ScrollReveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const [ref, inView] = useInView();
  const delayClass = delay ? ` reveal-delay-${delay}` : '';
  return (
    <Tag ref={ref} className={`reveal${delayClass} ${inView ? 'visible' : ''} ${className}`}>
      {children}
    </Tag>
  );
};

export const ImageReveal = ({ src, alt, className = '' }) => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  return (
    <div ref={ref} className={`lux-image-wrap img-reveal ${inView ? 'visible' : ''} ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    </div>
  );
};

export default ScrollReveal;
